import {
  Button,
  Divider,
  Paper,
  Stack,
  Typography,
  IconButton,
  Skeleton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useContext, useEffect, useState } from 'react';
import ReactAudioPlayer from 'react-audio-player';
import ReactPlayer from 'react-player';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import { fetchInterviewResult } from 'src/services/interview.service';
import LoadingDots from 'src/components/loading-dots';
import PropTypes from 'prop-types';
import FallbackUI from '../fallback-ui';
import HorizontalBar from '../horizontal-bar-chart';
import { CandidateProfileContext } from '../context/candidate-profile-context';

const useStyles = makeStyles(theme => ({
  leftContainerAnalysis: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    padding: '18px 15px',
    gap: 34,
    width: '30%',
  },
  rightContainerAnalysis: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    padding: '18px 15px',
    paddingBottom: 0,
    width: '100%',
  },
  questionsDiv: {
    gap: 24,
  },
  questionItem: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
    padding: 20,
    gap: 16,
  },
  candidateCapturesDiv: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
    padding: 20,
    gap: 16,
  },
  transcript: {
    lineHeight: 'normal',
    color: theme.palette.content.primaryBlack2,
  },
  scoreBtn: {
    color: theme.palette.content.accent1,
    background: theme.palette.background.primaryAccent2,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryAccent2,
    },
  },
  disabledScoreBtn: {
    color: theme.palette.content.primaryBlack3,
    background: theme.palette.background.primaryGrey2,
    fontWeight: 600,
  },
  recordedBtn: {
    color: theme.palette.content.primaryBlack1,
    background: theme.palette.background.primaryGrey1,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryGrey1,
    },
  },
  crossDiv: {
    background: theme.palette.background.primaryGrey3,
    padding: 4,
    borderRadius: 32,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    top: '4%',
    left: '92%',
    cursor: 'pointer',
    zIndex: 10,
  },
}));

function getRandomElements(arr, numElements) {
  // Make a copy of the array to avoid modifying the original array
  const shuffledArray = arr.slice();

  // Fisher-Yates shuffle algorithm
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }

  // Select the first 'numElements' elements
  return shuffledArray.slice(0, numElements);
}

const scoreState = 'scoreState';
const videoState = 'videoState';
const screenSharingState = 'screenSharingState';
const audioState = 'audioState';

export default function SkillAssessment({ jobId }) {
  // hooks
  const classes = useStyles();
  const candidateDetails = useContext(CandidateProfileContext)?.obj;

  // states
  const [interviewDetails, setInterviewDetails] = useState(null);
  const [recordedDataState, setRecordedDataState] = useState(null);
  const [skillAnalysis, setSkillAnalysis] = useState(null);
  const [candidateCaptures, setCandidateCaputures] = useState(null);
  const [aiInterviewLoader, setAiInterviewLoader] = useState(true);
  const [interviewStatus, setInterviewStatus] = useState('UNATTEMPTED');

  // effects
  useEffect(() => {
    setAiInterviewLoader(true);
    fetchInterviewResult(
      jobId,
      candidateDetails?.interview_details?.interview_id,
    )
      .then(res => {
        setSkillAnalysis(res?.body?.skill_scores);
        setInterviewStatus(res?.body?.interview_status);

        const _questions = res?.body?.questions.map(question => ({
          questionId: question?.question_id,
          question: question?.text,
          solution: null,
        }));

        const _solutions = res?.body?.solutions;

        _solutions?.forEach(solution => {
          const currentQuestion = _questions.find(
            item => item.questionId === solution.question_id,
          );
          if (currentQuestion) {
            const _solution = {
              transcript: solution?.transcript,
              candidateCaptures: solution?.candidate_captures,
              audioUrl: solution?.audio_url,
              score: solution?.score,
              isEvaluated: solution?.is_evaluated,
              video: solution?.video_record_url,
              screenRecording: res?.body?.screen_recordings?.find(
                item => item?.question_id === solution?.questionId,
              )?.screen_recording_url,
            };

            currentQuestion.solution = _solution;
          }
        });

        setInterviewDetails(_questions);

        const _candidateCaptures = [];

        _questions?.forEach(item => {
          item?.solution?.candidateCaptures?.forEach(capture =>
            _candidateCaptures.push(capture),
          );
        });

        if (_candidateCaptures.length > 8) {
          const selectedElements = getRandomElements(_candidateCaptures, 8);
          setCandidateCaputures(selectedElements);
        } else {
          setCandidateCaputures(_candidateCaptures);
        }

        const _recordedState = _questions?.map(item => {
          const obj = {
            state: scoreState,
          };
          return obj;
        });

        setRecordedDataState(_recordedState);
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setAiInterviewLoader(false);
      });
  }, [candidateDetails?.interview_details?.interview_id, jobId]);

  const AiInterviewSkeleton = (
    <Stack>
      <Stack
        height="220px"
        direction="row"
        alignItems="center"
        width="100%"
        spacing="12px"
        mb="32px"
      >
        <Stack height="100%" width="30%" spacing="48px" py="20px" px="12px">
          <Stack spacing="21px">
            <Skeleton width={39} height={9} />
            <Skeleton width={44} height={18} />
          </Stack>
          <Stack spacing="21px">
            <Skeleton width={39} height={9} />
            <Skeleton width={44} height={18} />
          </Stack>
        </Stack>
        <Stack height="100%" width="70%" py="12px" px="12px">
          <Stack spacing="20px">
            <Skeleton width={77} height={10} />
            <Stack spacing="24px">
              <Stack spacing="6px" direction="row">
                <Stack spacing="12px" direction="row">
                  <Skeleton width={62} height={10} />
                  <Skeleton width={106} height={10} />
                </Stack>
                <Skeleton width={15} height={10} />
              </Stack>
              <Stack spacing="10px" direction="row" alignItems="center">
                <Stack spacing="12px">
                  <Skeleton width={62} height={10} />
                  <Skeleton width={45} height={10} />
                </Stack>
                <Skeleton width={164} height={10} />
                <Skeleton width={15} height={10} />
              </Stack>
              <Stack direction="row">
                <Skeleton width={62} height={10} />
                <Skeleton width="65%" height={10} />
                <Skeleton width={15} height={10} />
              </Stack>
              <Stack direction="row">
                <Skeleton width={62} height={10} />
                <Skeleton width="75%" height={10} />
                <Skeleton width={15} height={10} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        width="100%"
        spacing="12px"
        mb="22px"
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing="8px"
          py="10px"
          px="12px"
          borderRadius="6px"
          border="1px solid #E8E8E8"
          width="100%"
        >
          <Iconify
            icon="tabler:user-square"
            sx={{
              color: theme => theme.palette.border.primaryBlack3,
            }}
          />
          <Skeleton width="100%" height={10} />
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing="8px"
          py="10px"
          px="12px"
          borderRadius="6px"
          border="1px solid #E8E8E8"
          width="100%"
        >
          <Iconify
            icon="tabler:device-mobile"
            sx={{
              color: theme => theme.palette.border.primaryBlack3,
            }}
          />
          <Skeleton width="100%" height={10} />
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing="8px"
          py="10px"
          px="12px"
          borderRadius="6px"
          border="1px solid #E8E8E8"
          width="100%"
        >
          <Iconify
            icon="tabler:clock-hour-4"
            sx={{
              color: theme => theme.palette.border.primaryBlack3,
            }}
          />
          <Skeleton width="100%" height={10} />
        </Stack>
      </Stack>
      <Stack
        py="24px"
        px="20px"
        borderRadius="6px"
        border="1px solid #E8E8E8"
        spacing="17px"
      >
        <Skeleton width="100%" height={10} />
        <Stack spacing="8px">
          <Stack spacing="11px">
            <Skeleton width="100%" height={10} />
            <Skeleton width="80%" height={10} />
            <Skeleton width="65%" height={10} />
            <Skeleton width="50%" height={10} />
          </Stack>
          <Stack direction="row" spacing="12px" height="36px">
            <Skeleton width={102} height="100%" sx={{ borderRadius: '6px' }} />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing="8px"
              px="20px"
              py="8px"
              bgcolor="#FAFAFA"
            >
              <Iconify
                icon="tabler:video"
                width={20}
                sx={{
                  color: theme => theme.palette.border.primaryBlack3,
                }}
              />
              <Skeleton width={32} height={10} />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing="8px"
              px="20px"
              py="8px"
              bgcolor="#FAFAFA"
            >
              <Iconify
                icon="tabler:device-imac"
                width={20}
                sx={{
                  color: theme => theme.palette.border.primaryBlack3,
                }}
              />
              <Skeleton width={85} height={10} />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing="8px"
              px="20px"
              py="8px"
              bgcolor="#FAFAFA"
            >
              <Iconify
                icon="tabler:microphone"
                width={20}
                sx={{
                  color: theme => theme.palette.border.primaryBlack3,
                }}
              />
              <Skeleton width={32} height={10} />
            </Stack>
          </Stack>
          <Skeleton width="100%" height={255} sx={{ borderRadius: '6px' }} />
        </Stack>
      </Stack>
    </Stack>
  );

  if (aiInterviewLoader)
    return (
      <Stack
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        Loading...
      </Stack>
    );

  return interviewStatus !== 'UNATTEMPTED' &&
    interviewDetails &&
    interviewDetails?.length > 0 ? (
    <Stack spacing="30px">
      <Stack direction="row" spacing="12px">
        <Stack className={classes.leftContainerAnalysis}>
          <Stack spacing="17px">
            <Stack spacing="10px" direction="row">
              <Typography variant="body1">Rank</Typography>
              <Image width="20px" src="/assets/circleI.svg" />
            </Stack>
            <Typography variant="h1">
              {candidateDetails?.interview_details?.rank || '_'}
            </Typography>
          </Stack>
          <Divider />
          <Stack spacing="17px">
            <Typography variant="body1">Overall Score</Typography>
            <Typography variant="h1">
              {candidateDetails?.interview_details?.total_score === null
                ? '_'
                : candidateDetails?.interview_details?.total_score}
            </Typography>
          </Stack>
        </Stack>
        <Stack className={classes.rightContainerAnalysis}>
          <Typography variant="subheading1">Analysis</Typography>
          {skillAnalysis ? (
            <HorizontalBar skillAnalysis={skillAnalysis} />
          ) : (
            <Stack
              sx={{ height: '25vh' }}
              spacing="12px"
              alignItems="center"
              justifyContent="center"
            >
              <Image width={48} height={48} src="/assets/no-data-found.svg" />
              <Typography variant="subheading2">No data</Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack className={classes.questionsDiv}>
        {interviewDetails?.map((item, index) => {
          const isActiveScore = () => {
            if (!item?.solution) return false;
            if (
              item?.solution?.isEvaluated === null ||
              item?.solution?.isEvaluated === undefined
            ) {
              return false;
            }
            return true;
          };

          const isActiveAudio = () => {
            if (!item?.solution?.audioUrl) return false;
            return true;
          };

          const isActiveScreenRecording = () => {
            if (!item?.solution?.screenRecording) return false;
            return true;
          };

          const isActiveVideo = () => {
            if (!item?.solution?.video) return false;
            return true;
          };

          return (
            <Stack key={index} className={classes.questionItem}>
              <Typography variant="subtitle1">
                Question {index + 1} : {item?.question}
              </Typography>
              {item?.solution &&
              (item?.solution?.isEvaluated !== null ||
                item?.solution?.isEvaluated !== undefined) ? (
                <Typography
                  variant="input"
                  color={!item?.solution?.isEvaluated && 'info'}
                  className={classes.transcript}
                >
                  {!item?.solution?.isEvaluated ? (
                    <Stack
                      sx={{
                        color: theme => theme.palette.content.accent1,
                      }}
                      direction="row"
                      alignItems="baseline"
                      spacing="2px"
                    >
                      Evaluation under process
                      <LoadingDots color="#5418EF" />
                    </Stack>
                  ) : (
                    `Transcript : ${item?.solution?.transcript}`
                  )}
                </Typography>
              ) : (
                <Typography
                  color={theme => theme.palette.content.primaryRed1}
                  variant="input"
                >
                  Unanswered
                </Typography>
              )}
              <Stack direction="row" spacing="12px">
                <Button
                  variant="contained"
                  size="small"
                  disabled={!isActiveScore()}
                  className={
                    isActiveScore()
                      ? classes.scoreBtn
                      : classes.disabledScoreBtn
                  }
                >
                  Score - {item?.solution?.score || 0}
                </Button>

                {item?.solution?.video && (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!isActiveVideo()}
                    className={classes.recordedBtn}
                    startIcon={
                      <Iconify
                        style={{ cursor: 'pointer' }}
                        icon="tabler:video"
                      />
                    }
                    sx={{
                      border: theme =>
                        recordedDataState[index].state === videoState &&
                        `1px solid ${theme.palette.border.primaryAccent1}`,
                    }}
                    onClick={() =>
                      setRecordedDataState(data => {
                        const dumData = [...data];
                        if (dumData[index].state === videoState) {
                          dumData[index].state = null;
                        } else {
                          dumData[index].state = videoState;
                        }
                        return dumData;
                      })
                    }
                  >
                    Video
                  </Button>
                )}
                {item?.solution?.screenRecording && (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!isActiveScreenRecording()}
                    className={classes.recordedBtn}
                    startIcon={<Iconify icon="tabler:screen-share" />}
                    sx={{
                      border: theme =>
                        recordedDataState[index].state === screenSharingState &&
                        `1px solid ${theme.palette.border.primaryAccent1}`,
                    }}
                    onClick={() =>
                      setRecordedDataState(data => {
                        const dumData = [...data];
                        if (dumData[index].state === screenSharingState) {
                          dumData[index].state = null;
                        } else {
                          dumData[index].state = screenSharingState;
                        }
                        return dumData;
                      })
                    }
                  >
                    Screen Sharing
                  </Button>
                )}
                {item?.solution?.audioUrl && (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!isActiveAudio()}
                    className={classes.recordedBtn}
                    startIcon={<Iconify icon="tabler:microphone" />}
                    sx={{
                      border: theme =>
                        recordedDataState[index].state === audioState &&
                        `1px solid ${theme.palette.border.primaryAccent1}`,
                    }}
                    onClick={() =>
                      setRecordedDataState(data => {
                        const dumData = [...data];
                        if (dumData[index].state === audioState) {
                          dumData[index].state = null;
                        } else {
                          dumData[index].state = audioState;
                        }
                        return dumData;
                      })
                    }
                  >
                    Audio
                  </Button>
                )}
              </Stack>
              <Stack>
                {recordedDataState[index].state === scoreState && <></>}
                {recordedDataState[index].state === videoState && (
                  <Stack position="relative">
                    <ReactPlayer
                      width="100%"
                      height="255px"
                      // style={{height:"100px"}}
                      controls
                      url={item?.solution?.video}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        right: '12px',
                        top: '12px',
                        bgcolor: theme => theme.palette.background.primaryGrey2,
                        color: theme => theme.palette.content.primaryBlack1,
                      }}
                      onClick={() => {
                        setRecordedDataState(data => {
                          const dumData = [...data];
                          dumData[index].state = null;
                          return dumData;
                        });
                      }}
                    >
                      <Iconify icon="tabler:x" />
                    </IconButton>
                  </Stack>
                )}
                {recordedDataState[index].state === screenSharingState && (
                  <Stack
                    spacing="5px"
                    sx={{ borderRadius: '6px', overflow: 'hidden' }}
                  >
                    <Stack
                      className={classes.crossDiv}
                      justifyContent="flex-end"
                      onClick={() =>
                        setRecordedDataState(data => {
                          const dumData = [...data];
                          dumData[index].state = scoreState;
                          return dumData;
                        })
                      }
                    >
                      <Iconify icon="tabler:x" />
                    </Stack>
                    <ReactPlayer
                      width="100%"
                      height="255px"
                      // style={{height:"100px"}}
                      controls
                      url={item?.solution?.screenRecording}
                    />
                  </Stack>
                )}
                {recordedDataState[index].state === audioState && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    position="relative"
                    backgroundColor="#F3F3F3"
                    borderRadius="24px"
                    paddingRight="28px"
                  >
                    <ReactAudioPlayer
                      src={item.solution?.audioUrl}
                      style={{
                        width: '100%',
                        height: '40px',
                      }}
                      controls
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        right: '8px',
                      }}
                      onClick={() => {
                        setRecordedDataState(data => {
                          const dumData = [...data];
                          dumData[index].state = null;
                          return dumData;
                        });
                      }}
                    >
                      <Iconify icon="tabler:x" />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
      {candidateCaptures && candidateCaptures?.length > 0 && (
        <Stack className={classes.candidateCapturesDiv}>
          <Typography variant="subtitle1">Proctor monitoring</Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '10px' }}>
            {candidateCaptures?.map((candidateCapture, index) => (
              <Paper
                key={index}
                sx={{
                  mr: '6px',
                  borderRadius: 1,
                  position: 'relative',
                }}
              >
                <Stack alignItems="center" sx={{ pb: 0.5 }}>
                  <Image
                    src={candidateCapture?.image_url}
                    sx={{
                      width: 150,
                      height: 100,
                      borderRadius: 1.5,
                      objectFit: 'contain',
                    }}
                    alt="image"
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  ) : (
    <FallbackUI content="No data found" />
  );
}

SkillAssessment.propTypes = {
  jobId: PropTypes.string.isRequired,
};
